import { useMemo, useState } from 'react';
import { CalendarDays, Clock, Copy, Check, ExternalLink, Play, Video } from 'lucide-react';
import { useI18n } from '../../context/useI18n';

const TZ_CO = 'America/Bogota';

function parseStart(notice) {
  const raw = notice?.meta?.start_time;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatChip(date, lang) {
  const locale = lang === 'en' ? 'en-US' : 'es-CO';
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: TZ_CO }).format(date);
  const day = new Intl.DateTimeFormat(locale, { day: '2-digit', timeZone: TZ_CO }).format(date);
  const month = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: TZ_CO }).format(date);
  return {
    weekday: weekday.replace('.', '').toUpperCase(),
    day,
    month: month.replace('.', '').toUpperCase(),
  };
}

function formatTime(date, lang) {
  const locale = lang === 'en' ? 'en-US' : 'es-CO';
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit', minute: '2-digit', hour12: true, timeZone: TZ_CO,
  }).format(date);
}

function sameDayInCo(a, b) {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ_CO });
  return fmt.format(a) === fmt.format(b);
}

/** Avisos de asignación de clase (tipo `live_class_host`) como tarjetas. */
export default function HostClassNoticeCards({ notices = [], programs = [], limit = 6 }) {
  const { t, lang } = useI18n();
  const [copiedId, setCopiedId] = useState(null);

  const programName = useMemo(() => {
    const map = new Map();
    (programs || []).forEach((p) => {
      const id = p.id || p.code || p.slug;
      if (id) map.set(String(id), p.name || p.title || p.nombre || String(id));
    });
    // Sin catálogo cargado (host sin gym) el id crudo `virtual_d28d` se muestra legible.
    return (id) => {
      if (!id) return null;
      const known = map.get(String(id));
      if (known) return known;
      return String(id).replace(/[_-]+/g, ' ').trim();
    };
  }, [programs]);

  const { classCards, plainNotices } = useMemo(() => {
    const cards = [];
    const plain = [];
    (notices || []).forEach((n) => {
      const start = parseStart(n);
      if (String(n?.tipo || '') === 'live_class_host' && start) {
        cards.push({ notice: n, start });
      } else {
        plain.push(n);
      }
    });
    const now = new Date();
    cards.sort((a, b) => {
      const aFuture = a.start >= now;
      const bFuture = b.start >= now;
      if (aFuture !== bFuture) return aFuture ? -1 : 1;
      return aFuture ? a.start - b.start : b.start - a.start;
    });
    return { classCards: cards.slice(0, limit), plainNotices: plain.slice(0, 3) };
  }, [notices, limit]);

  if (!classCards.length && !plainNotices.length) return null;

  const now = new Date();

  const copyLink = async (id, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId((prev) => (prev === id ? null : prev)), 2000);
    } catch { /* clipboard no disponible */ }
  };

  return (
    <div className="surface-readable ui-light-panel rounded-xl border border-slate-200 p-3 sm:p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <CalendarDays className="w-4 h-4" style={{ color: '#0f172a' }} aria-hidden />
        <h3 className="font-bold text-sm m-0" style={{ color: '#0f172a' }}>
          {t('live.host_notifications', 'Avisos de tus clases')}
        </h3>
      </div>

      {classCards.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {classCards.map(({ notice, start }) => {
            const meta = notice.meta || {};
            const chip = formatChip(start, lang);
            const isToday = sameDayInCo(start, now);
            const isPast = start < now && !isToday;
            const statusLabel = isToday
              ? t('live.host_notice_today', 'Hoy')
              : (isPast
                ? t('live.host_notice_done', 'Finalizada')
                : t('live.host_notice_upcoming', 'Próxima'));
            const statusClass = isToday
              ? 'bg-amber-100 text-amber-800 border-amber-200'
              : (isPast
                ? 'bg-stone-100 text-stone-600 border-stone-200'
                : 'bg-emerald-100 text-emerald-800 border-emerald-200');
            const program = programName(meta.program_id);
            const joinUrl = meta.start_url || meta.zoom_link || '';
            const shareUrl = meta.zoom_link || '';

            return (
              <article
                key={notice.id}
                className={`d28d-live-classes__class-card surface-readable ui-light-panel flex flex-col gap-3 transition-shadow hover:shadow-lg ${isPast ? 'opacity-75' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-center leading-tight">
                    <div className="text-[10px] font-bold tracking-wide" style={{ color: '#64748b' }}>{chip.weekday}</div>
                    <div className="text-lg font-extrabold" style={{ color: '#0f172a' }}>{chip.day}</div>
                    <div className="text-[10px] font-bold tracking-wide" style={{ color: '#64748b' }}>{chip.month}</div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${statusClass}`}>
                        {statusLabel}
                      </span>
                      {program ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-slate-200 bg-white text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#475569' }}>
                          {program}
                        </span>
                      ) : null}
                    </div>
                    <h4 className="text-sm font-bold m-0 break-words" style={{ color: '#0f172a' }}>
                      {meta.class_title || t('live.host_class_fallback', 'Clase')}
                    </h4>
                    <p className="text-xs m-0 mt-1 inline-flex items-center gap-1" style={{ color: '#475569' }}>
                      <Clock className="w-3.5 h-3.5" aria-hidden />
                      {formatTime(start, lang)}
                    </p>
                    {meta.host_zoom_email ? (
                      <p className="text-xs m-0 mt-1 inline-flex items-center gap-1 break-all" style={{ color: '#475569' }}>
                        <Video className="w-3.5 h-3.5 shrink-0" aria-hidden />
                        {meta.host_zoom_email}
                      </p>
                    ) : null}
                  </div>
                </div>

                {(joinUrl || shareUrl) ? (
                  <div className="flex flex-wrap gap-2 mt-auto pt-1">
                    {joinUrl ? (
                      <a
                        href={joinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary text-xs inline-flex items-center gap-1.5"
                      >
                        <Play className="w-3.5 h-3.5" aria-hidden />
                        {meta.start_url
                          ? t('live.host_notice_start', 'Iniciar reunión')
                          : t('live.host_notice_join', 'Entrar a Zoom')}
                      </a>
                    ) : null}
                    {shareUrl ? (
                      <>
                        <a
                          href={shareUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary text-xs inline-flex items-center gap-1.5"
                        >
                          <ExternalLink className="w-3.5 h-3.5" aria-hidden />
                          {t('live.host_notice_link', 'Enlace participantes')}
                        </a>
                        <button
                          type="button"
                          className="btn btn-secondary text-xs inline-flex items-center justify-center px-2"
                          onClick={() => copyLink(notice.id, shareUrl)}
                          title={copiedId === notice.id
                            ? t('live.host_notice_copied', 'Copiado')
                            : t('live.host_notice_copy', 'Copiar')}
                          aria-label={t('live.host_notice_copy', 'Copiar')}
                        >
                          {copiedId === notice.id
                            ? <Check className="w-3.5 h-3.5" aria-hidden />
                            : <Copy className="w-3.5 h-3.5" aria-hidden />}
                        </button>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}

      {plainNotices.length > 0 && (
        <ul className="text-sm space-y-2 mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
          {plainNotices.map((n) => (
            <li key={n.id} className="whitespace-pre-wrap border-b border-slate-100 pb-2 last:border-0 last:pb-0" style={{ color: '#334155' }}>
              {n.mensaje}
            </li>
          ))}
        </ul>
      )}

      {classCards.length > 0 ? (
        <p className="text-xs mt-2 m-0" style={{ color: '#64748b' }}>
          {t('live.host_notice_hint', 'Abre la clase en el calendario para ver la rutina y registrar observaciones.')}
        </p>
      ) : null}
    </div>
  );
}
